import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  sendSingleNotificationService,
  sendBulkNotificationService,
  fetchAllNotificationsService,
  sendWhatsAppSingleNotificationService,
  sendWhatsAppBulkNotificationService,
  sendPromoNotificationService,
} from "../../apis/notifications";
import { cleanObject } from "../../helpers";

interface NotificationState {
  allNotifications: any[];
  loading: boolean;
  error: string | null;
  sendingSingle: boolean;
  sendingBulk: boolean;
  sendingWhatsAppSingle: boolean;
  sendingWhatsAppBulk: boolean;
  sendingPromo: boolean;
}

const initialState: NotificationState = {
  allNotifications: [],
  loading: false,
  error: null,
  sendingSingle: false,
  sendingBulk: false,
  sendingWhatsAppSingle: false,
  sendingWhatsAppBulk: false,
  sendingPromo: false,
};

// Fetch all notifications
export const fetchAllNotifications = createAsyncThunk(
  "notifications/fetchAll",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await fetchAllNotificationsService(cleanObject(payload));
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to fetch notifications",
      );
      return rejectWithValue(error.response?.data?.message ?? "Fetch failed");
    }
  },
);

// Send single notification
export const sendSingleNotification = createAsyncThunk(
  "notifications/sendSingle",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await sendSingleNotificationService(payload);
      toast.success("Single notification sent successfully");
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to send notification",
      );
      return rejectWithValue(error.response?.data?.message ?? "Send failed");
    }
  },
);

// Send bulk notification
export const sendBulkNotification = createAsyncThunk(
  "notifications/sendBulk",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await sendBulkNotificationService(payload);
      toast.success("Bulk notification sent successfully");
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to send notification",
      );
      return rejectWithValue(error.response?.data?.message ?? "Send failed");
    }
  },
);

// Send WhatsApp single notification
export const sendWhatsAppSingleNotification = createAsyncThunk(
  "notifications/sendWhatsAppSingle",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await sendWhatsAppSingleNotificationService(payload);
      toast.success("WhatsApp notification sent successfully");
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to send WhatsApp notification",
      );
      return rejectWithValue(error.response?.data?.message ?? "Send failed");
    }
  },
);

// Send WhatsApp bulk notification
export const sendWhatsAppBulkNotification = createAsyncThunk(
  "notifications/sendWhatsAppBulk",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await sendWhatsAppBulkNotificationService(payload);
      toast.success("WhatsApp bulk notification sent successfully");
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to send WhatsApp notification",
      );
      return rejectWithValue(error.response?.data?.message ?? "Send failed");
    }
  },
);

// Send promo notification to anonymous installs
export const sendPromoNotification = createAsyncThunk(
  "notifications/sendPromo",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await sendPromoNotificationService(payload);
      const message =
        response?.data?.message ?? "Promo notification sent successfully";

      // Show warning if no installs available, success otherwise
      if (message.toLowerCase().includes("no anonymous installs")) {
        toast.warning(message);
      } else {
        toast.success(message);
      }

      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to send promo notification",
      );
      return rejectWithValue(error.response?.data?.message ?? "Send failed");
    }
  },
);

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    resetNotifications: (state) => {
      state.allNotifications = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchAllNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.allNotifications = action.payload;
      })
      .addCase(
        fetchAllNotifications.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        },
      )

      // Send single
      .addCase(sendSingleNotification.pending, (state) => {
        state.sendingSingle = true;
        state.error = null;
      })
      .addCase(sendSingleNotification.fulfilled, (state, action) => {
        state.sendingSingle = false;
      })
      .addCase(
        sendSingleNotification.rejected,
        (state, action: PayloadAction<any>) => {
          state.sendingSingle = false;
          state.error = action.payload;
        },
      )

      // Send bulk
      .addCase(sendBulkNotification.pending, (state) => {
        state.sendingBulk = true;
        state.error = null;
      })
      .addCase(sendBulkNotification.fulfilled, (state, action) => {
        state.sendingBulk = false;
      })
      .addCase(
        sendBulkNotification.rejected,
        (state, action: PayloadAction<any>) => {
          state.sendingBulk = false;
          state.error = action.payload;
        },
      )

      // Send WhatsApp single
      .addCase(sendWhatsAppSingleNotification.pending, (state) => {
        state.sendingWhatsAppSingle = true;
        state.error = null;
      })
      .addCase(sendWhatsAppSingleNotification.fulfilled, (state, action) => {
        state.sendingWhatsAppSingle = false;
      })
      .addCase(
        sendWhatsAppSingleNotification.rejected,
        (state, action: PayloadAction<any>) => {
          state.sendingWhatsAppSingle = false;
          state.error = action.payload;
        },
      )

      // Send WhatsApp bulk
      .addCase(sendWhatsAppBulkNotification.pending, (state) => {
        state.sendingWhatsAppBulk = true;
        state.error = null;
      })
      .addCase(sendWhatsAppBulkNotification.fulfilled, (state, action) => {
        state.sendingWhatsAppBulk = false;
      })
      .addCase(
        sendWhatsAppBulkNotification.rejected,
        (state, action: PayloadAction<any>) => {
          state.sendingWhatsAppBulk = false;
          state.error = action.payload;
        },
      )

      // Send Promo
      .addCase(sendPromoNotification.pending, (state) => {
        state.sendingPromo = true;
        state.error = null;
      })
      .addCase(sendPromoNotification.fulfilled, (state, action) => {
        state.sendingPromo = false;
      })
      .addCase(
        sendPromoNotification.rejected,
        (state, action: PayloadAction<any>) => {
          state.sendingPromo = false;
          state.error = action.payload;
        },
      );
  },
});

export const { resetNotifications } = notificationSlice.actions;
export default notificationSlice.reducer;
